import React, { useEffect, useRef, useState } from "react";
import { mixUrl, takeChannel } from "@/lib/audioPool";
import { AnimatePresence, motion } from "framer-motion";
import { Pencil, SkipForward } from "lucide-react";
import NexWordmark from "@/components/NexWordmark";
import IntroLogo from "./IntroLogo";
import IntroStats from "./IntroStats";
import { IntroParticles, IntroSweep, IntroGrid } from "./IntroFx";

// beat timings in ms: title, stats, powered-by, exit, done
const T = { title: 900, stats: 2300, powered: 3900, exit: 4800, done: 5400 };
const NARRATION_DELAY = 350;
const MAX_MS = 12000;

export default function CinematicIntro({ intro, audioUrl, musicUrl, phases = [], isAdmin, onEdit, onDone }) {
  const [beat, setBeat] = useState(0);
  const audioRef = useRef(null);
  const musicRef = useRef(null);

  useEffect(() => {
    const timers = [
      setTimeout(() => setBeat(1), T.title),
      setTimeout(() => setBeat(2), T.stats),
    ];
    let scheduled = false;
    let cancelled = false;
    // Stretch the back half of the intro so the narration never gets cut off (max 12s).
    const scheduleEnd = (voiceSec) => {
      if (scheduled || cancelled) return;
      scheduled = true;
      const total = Math.min(MAX_MS, Math.max(T.done, NARRATION_DELAY + voiceSec * 1000 + 700));
      const shift = total - T.done;
      timers.push(
        setTimeout(() => setBeat(3), T.powered + shift),
        setTimeout(() => setBeat(4), T.exit + shift),
        setTimeout(() => onDone(), total),
      );
    };
    const mh = musicUrl ? takeChannel("music", mixUrl(musicUrl, 0.55)) : null;
    const ah = audioUrl ? takeChannel("voice", audioUrl) : null;
    const m = mh?.el || null;
    const a = ah?.el || null;
    musicRef.current = m;
    audioRef.current = a;
    mh?.play().catch(() => {});
    if (a) {
      a.preload = "auto";
      a.addEventListener("loadedmetadata", () => scheduleEnd(Number.isFinite(a.duration) ? a.duration : 0));
      a.addEventListener("error", () => scheduleEnd(0));
      timers.push(setTimeout(() => ah.play().catch(() => {}), NARRATION_DELAY));
      timers.push(setTimeout(() => scheduleEnd(0), 3500));
    } else {
      scheduleEnd(0);
    }
    // Stop this run's audio on cleanup so re-runs (StrictMode, prop changes) never overlap.
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      [mh, ah].forEach((h) => h?.release());
    };
  }, [audioUrl, musicUrl, onDone]);

  const halt = () => { try { audioRef.current?.pause(); musicRef.current?.pause(); } catch {} };
  const skip = () => { halt(); onDone(); };
  const edit = () => { halt(); onEdit(); };

  return (
    <motion.div
      data-testid="veroogle-intro"
      className="fixed inset-0 z-[120] overflow-hidden bg-[#03060F] text-white select-none"
      animate={beat === 4 ? { opacity: 0 } : { opacity: 1 }}
      transition={{ duration: 0.55, ease: "easeIn" }}
    >
      <IntroGrid />
      <IntroSweep />
      <IntroParticles />

      <motion.div
        className="relative z-10 h-full w-full flex flex-col items-center justify-center px-6"
        animate={beat === 4 ? { scale: 6, filter: "blur(12px)" } : { scale: 1, filter: "blur(0px)" }}
        transition={{ type: "tween", duration: 0.6, ease: [0.7, 0, 0.84, 0] }}
      >
        <motion.div
          className="flex flex-col items-center"
          animate={{ y: beat >= 2 ? -24 : 0, scale: beat >= 2 ? 0.86 : 1 }}
          transition={{ type: "spring", stiffness: 120, damping: 18 }}
        >
          <IntroLogo name={intro.headline} logoUrl={intro.logo_url} />
          <IntroHeadline text={intro.headline} show={beat >= 1} />
          <AnimatePresence>
            {beat >= 1 && (
              <motion.div
                data-testid="veroogle-intro-tagline"
                initial={{ opacity: 0, letterSpacing: "0.6em" }}
                animate={{ opacity: 1, letterSpacing: "0.32em" }}
                transition={{ delay: 0.55, duration: 0.7 }}
                className="mt-3 font-mono text-[11px] sm:text-xs uppercase text-cyan-300/90 text-center"
              >
                {intro.tagline}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <AnimatePresence>{beat >= 2 && <IntroStats stats={intro.stats} />}</AnimatePresence>

        <AnimatePresence>
          {beat >= 2 && phases.length > 0 && (
            <motion.div className="mt-8 flex flex-wrap justify-center gap-2" data-testid="veroogle-intro-phases">
              {phases.map((p, i) => (
                <motion.span
                  key={p}
                  initial={{ opacity: 0, y: 10, borderColor: "rgba(255,255,255,0.08)" }}
                  animate={{ opacity: 1, y: 0, borderColor: ["rgba(255,255,255,0.08)", "rgba(16,185,129,0.8)", "rgba(6,182,212,0.35)"] }}
                  transition={{ delay: 0.5 + i * 0.14, duration: 0.6 }}
                  className="px-3 py-1 rounded-full border bg-white/[0.03] font-mono text-[10px] tracking-[0.16em] uppercase text-slate-300"
                >
                  <span className="text-emerald-400 mr-1.5">0{i + 1}</span>{p}
                </motion.span>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {beat >= 3 && (
            <motion.div
              data-testid="veroogle-intro-powered"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="absolute bottom-[12%] flex items-center gap-3"
            >
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-400">Powered by</span>
              <NexWordmark size="text-2xl" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-400">engines</span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <div className="absolute top-5 right-5 z-20 flex items-center gap-2">
        {isAdmin && (
          <button onClick={edit} data-testid="veroogle-intro-edit" aria-label="Edit intro"
            className="p-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-cyan-300 hover:border-cyan-400/50 transition-colors">
            <Pencil className="w-4 h-4" />
          </button>
        )}
        <button onClick={skip} data-testid="veroogle-intro-skip"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-white/30 font-mono text-[10px] tracking-[0.16em] uppercase transition-colors">
          Skip <SkipForward className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
}

function IntroHeadline({ text, show }) {
  return (
    <div className="relative mt-6 pb-2 leading-[1.15] text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight" data-testid="veroogle-intro-headline">
      {show && (
        <>
          <motion.span aria-hidden className="absolute inset-0 text-cyan-400/70 mix-blend-screen"
            initial={{ x: 0, opacity: 0 }} animate={{ x: [-6, 4, -2, 0], opacity: [0, 0.9, 0.6, 0] }}
            transition={{ delay: 0.35, duration: 0.5 }}>{text}</motion.span>
          <motion.span aria-hidden className="absolute inset-0 text-rose-500/70 mix-blend-screen"
            initial={{ x: 0, opacity: 0 }} animate={{ x: [6, -4, 2, 0], opacity: [0, 0.9, 0.6, 0] }}
            transition={{ delay: 0.35, duration: 0.5 }}>{text}</motion.span>
        </>
      )}
      <span className="relative flex flex-wrap justify-center">
        {text.split("").map((ch, i) => (
          <motion.span
            key={`${ch}-${i}`}
            initial={{ opacity: 0, y: 28, filter: "blur(10px)" }}
            animate={show ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
            transition={{ type: "tween", delay: i * 0.05, duration: 0.45, ease: "easeOut" }}
            className="bg-gradient-to-b from-white via-white to-cyan-200 bg-clip-text text-transparent"
          >
            {ch === " " ? "\u00A0" : ch}
          </motion.span>
        ))}
      </span>
    </div>
  );
}
